/**
 * @file utils.js
 * Contains shared utility functions used across the application.
 */

/**
 * Generates a random 6-character booking code.
 * @returns {string} The generated booking code.
 */
export function generateBookingCode() {
  const chars = "ABCDEFGHIJKLMNPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Initializes the user's timezone in a hidden field.
 */
export function initTimezone() {
  const timezoneElement = document.getElementById("user_timezone");
  if (timezoneElement) {
    timezoneElement.value = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}

/**
 * Updates the available month options based on a validation rule.
 * This version removes/re-adds options to support iOS.
 * @param {HTMLSelectElement} monthSelect - The month dropdown element.
 * @param {number} year - The currently selected year.
 * @param {function(Date): boolean} isInvalidDate - A function that returns true if a date is invalid.
 */
export function updateValidMonths(monthSelect, year, isInvalidDate) {
  const originalValue = monthSelect.value;
  const placeholder = monthSelect.options[0];
  const allMonths = Array.from(monthSelect.options)
    .slice(1)
    .map((opt) => ({ value: opt.value, text: opt.textContent }));

  monthSelect.innerHTML = "";
  monthSelect.appendChild(placeholder);

  const isPastCheck = isInvalidDate(new Date("1970-01-01"));

  const checkMonthValidity = (month) => {
    let testDate;
    if (isPastCheck) {
      testDate = new Date(year, month, 0); // Use last day of month for past checks
    } else {
      testDate = new Date(year, month - 1, 1); // Use first day of month for future checks
    }
    return isInvalidDate(testDate);
  };

  allMonths.forEach((monthInfo) => {
    const month = parseInt(monthInfo.value);
    if (!checkMonthValidity(month)) {
      const option = document.createElement("option");
      option.value = monthInfo.value;
      option.textContent = monthInfo.text;
      monthSelect.appendChild(option);
    }
  });

  monthSelect.value = originalValue;
  if (monthSelect.selectedIndex === -1 || monthSelect.value === "") {
    monthSelect.value = "";
  }
}

/**
 * Updates the available day options based on a validation rule.
 * This version removes/re-adds options to support iOS.
 * @param {HTMLSelectElement} daySelect - The day dropdown element.
 * @param {number} month - The currently selected month.
 * @param {number} year - The currently selected year.
 * @param {function(Date): boolean} isInvalidDate - A function that returns true if a date is invalid.
 */
export function updateValidDays(daySelect, month, year, isInvalidDate) {
  const daysInMonth = new Date(year, month, 0).getDate();
  const originalValue = daySelect.value;
  const placeholder = daySelect.options[0];

  daySelect.innerHTML = "";
  daySelect.appendChild(placeholder);

  for (let day = 1; day <= daysInMonth; day++) {
    const testDate = new Date(year, month - 1, day);
    if (!isInvalidDate(testDate)) {
      const option = document.createElement("option");
      option.value = day;
      option.textContent = day;
      daySelect.appendChild(option);
    }
  }

  daySelect.value = originalValue;
  if (daySelect.selectedIndex === -1 || daySelect.value === "") {
    daySelect.value = "";
  }
}
